import React, { useState, useRef } from "react";
import * as XLSX from "xlsx";
import axios from "axios";
import Popup from "../../../Popup";
import showToast from "../../../utils/toast";
import { BoardMedium_URL } from "../../config";
import "./UploadAnswersPopup.css";

const UploadAnswersPopup = ({
  isOpen,
  onClose,
  onSuccess,
  boardId,
  selectedMedium,
  selectedClass,
  selectedSubject,
  selectedChapter,
  selectedType,
  allQuestionTypes = [],
}) => {
  const [fileName, setFileName] = useState("");
  const [parsedData, setParsedData] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [submissionResult, setSubmissionResult] = useState(null);
  const fileInputRef = useRef(null);

  if (!isOpen) return null;

  const getTypeName = (typeId) => {
    const found = allQuestionTypes.find(
      (t) => String(t.type_id) === String(typeId)
    );
    return found ? found.type_name : `Type ${typeId}`;
  };

  const handleFileChange = (e) => {
    const uploadedFile = e.target.files[0];
    if (!uploadedFile) return;
    processFile(uploadedFile);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processFile(e.dataTransfer.files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const calculateOptionSort = (rawAns, optA, optB, optC, optD) => {
    let optionSort = null;
    let correctOption = "";
    const cleanAns = String(rawAns || "").trim().toLowerCase();

    if (
      cleanAns === "a" ||
      cleanAns === "option a" ||
      cleanAns === "1" ||
      (optA && cleanAns === String(optA).trim().toLowerCase())
    ) {
      optionSort = 1;
      correctOption = "A";
    } else if (
      cleanAns === "b" ||
      cleanAns === "option b" ||
      cleanAns === "2" ||
      (optB && cleanAns === String(optB).trim().toLowerCase())
    ) {
      optionSort = 2;
      correctOption = "B";
    } else if (
      cleanAns === "c" ||
      cleanAns === "option c" ||
      cleanAns === "3" ||
      (optC && cleanAns === String(optC).trim().toLowerCase())
    ) {
      optionSort = 3;
      correctOption = "C";
    } else if (
      cleanAns === "d" ||
      cleanAns === "option d" ||
      cleanAns === "4" ||
      (optD && cleanAns === String(optD).trim().toLowerCase())
    ) {
      optionSort = 4;
      correctOption = "D";
    } else {
      if (/^option\s*a\b/i.test(cleanAns) || cleanAns.startsWith("a")) {
        optionSort = 1;
        correctOption = "A";
      } else if (/^option\s*b\b/i.test(cleanAns) || cleanAns.startsWith("b")) {
        optionSort = 2;
        correctOption = "B";
      } else if (/^option\s*c\b/i.test(cleanAns) || cleanAns.startsWith("c")) {
        optionSort = 3;
        correctOption = "C";
      } else if (/^option\s*d\b/i.test(cleanAns) || cleanAns.startsWith("d")) {
        optionSort = 4;
        correctOption = "D";
      }
    }

    return { optionSort, correctOption };
  };

  const processFile = (selectedFile) => {
    setFileName(selectedFile.name);
    setIsProcessing(true);
    setSubmissionResult(null);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: "binary" });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const rawData = XLSX.utils.sheet_to_json(ws, { defval: "" });

        if (!rawData || rawData.length === 0) {
          showToast.error("The uploaded Excel sheet is empty!");
          setIsProcessing(false);
          return;
        }

        // Validate required columns (Question ID and Correct Answer)
        const firstRow = rawData[0] || {};
        const availableHeaders = Object.keys(firstRow).map((k) =>
          k.trim().toLowerCase()
        );

        const hasColumn = (aliases) =>
          aliases.some((alias) =>
            availableHeaders.includes(alias.trim().toLowerCase())
          );

        const missingCols = [];
        if (
          !hasColumn([
            "Question ID",
            "question_id",
            "question id",
            "QuestionId",
            "ID",
            "id",
            "S.No",
            "sno",
          ])
        ) {
          missingCols.push("Question ID");
        }

        if (
          !hasColumn([
            "Correct Answer",
            "correct_answer",
            "correct answer",
            "CorrectAnswer",
            "Answer",
            "answer",
            "Correct",
            "correct",
          ])
        ) {
          missingCols.push("Correct Answer");
        }

        if (missingCols.length > 0) {
          showToast.error(
            `Your uploaded Excel sheet is not in the required format. Missing: ${missingCols.join(", ")}. Please check the format and try again!`,
            { duration: 5000 }
          );
          setFileName("");
          setParsedData([]);
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
          setIsProcessing(false);
          return;
        }

        const processed = rawData.map((row, idx) => {
          const getVal = (possibleKeys) => {
            for (const k of possibleKeys) {
              if (row[k] !== undefined && row[k] !== "") return row[k];
              const foundKey = Object.keys(row).find(
                (rk) => rk.trim().toLowerCase() === k.toLowerCase()
              );
              if (
                foundKey &&
                row[foundKey] !== undefined &&
                row[foundKey] !== ""
              ) {
                return row[foundKey];
              }
            }
            return "";
          };

          const questionId = getVal([
            "Question ID",
            "question_id",
            "question id",
            "QuestionId",
            "ID",
            "id",
            "S.No",
            "sno",
          ]);
          const chapterName = getVal([
            "Chapter Name",
            "chapter_name",
            "chapter",
            "Chapter",
          ]);
          const questionText = getVal([
            "Question",
            "question_text",
            "question",
            "Question Text",
          ]);
          const optA = String(
            getVal([
              "Option A",
              "option_a",
              "option a",
              "OptionA",
              "A",
              "a",
              "opt_a",
            ])
          );
          const optB = String(
            getVal([
              "Option B",
              "option_b",
              "option b",
              "OptionB",
              "B",
              "b",
              "opt_b",
            ])
          );
          const optC = String(
            getVal([
              "Option C",
              "option_c",
              "option c",
              "OptionC",
              "C",
              "c",
              "opt_c",
            ])
          );
          const optD = String(
            getVal([
              "Option D",
              "option_d",
              "option d",
              "OptionD",
              "D",
              "d",
              "opt_d",
            ])
          );
          const rawAns = String(
            getVal([
              "Correct Answer",
              "correct_answer",
              "correct answer",
              "CorrectAnswer",
              "Answer",
              "answer",
              "Correct",
              "correct",
            ])
          );
          const explanation = String(
            getVal([
              "Answer Explanation",
              "answer_explanation",
              "answer explanation",
              "Explanation",
              "explanation",
              "Solution",
              "solution",
            ])
          );

          const { optionSort, correctOption } = calculateOptionSort(
            rawAns,
            optA,
            optB,
            optC,
            optD
          );

          return {
            id: `row-${idx}-${Date.now()}`,
            row_index: idx + 1,
            question_id: questionId || "",
            chapter_name: chapterName || "",
            question_text: questionText || "",
            option_a: optA,
            option_b: optB,
            option_c: optC,
            option_d: optD,
            correct_answer: rawAns,
            answer_explanation: explanation || "",
            option_sort: optionSort,
            correct_option: correctOption,
            board_id: boardId ? parseInt(boardId) : null,
            medium_id: selectedMedium ? parseInt(selectedMedium) : null,
            class_id: selectedClass ? parseInt(selectedClass) : null,
            subject_id: selectedSubject ? parseInt(selectedSubject) : null,
            chapter_id: selectedChapter ? parseInt(selectedChapter) : null,
            question_typeid: selectedType ? parseInt(selectedType) : null,
          };
        });

        setParsedData(processed);
        showToast.success(
          `Loaded ${processed.length} questions from ${selectedFile.name}`
        );
      } catch (err) {
        console.error("Error reading excel file:", err);
        showToast.error("Failed to parse Excel file. Please check file format.");
      } finally {
        setIsProcessing(false);
      }
    };

    reader.readAsBinaryString(selectedFile);
  };

  // Delete a specific row
  const handleDeleteRow = (indexToDelete) => {
    setParsedData((prev) => {
      const updated = prev.filter((_, idx) => idx !== indexToDelete);
      return updated.map((item, idx) => ({ ...item, row_index: idx + 1 }));
    });
    showToast.success("Row deleted");
  };

  // Submit handler - calls bulk-update-answers API
  const handleSubmit = async () => {
    if (!parsedData || parsedData.length === 0) {
      showToast.error("Please upload an Excel file to preview first.");
      return;
    }

    const outputJsonArray = parsedData.map((item) => ({
      question_id: item.question_id,
      chapter_name: item.chapter_name,
      question_text: item.question_text,
      option_a: item.option_a,
      option_b: item.option_b,
      option_c: item.option_c,
      option_d: item.option_d,
      correct_answer: item.correct_answer,
      answer_explanation: item.answer_explanation || "",
      option_sort: item.option_sort,
      correct_option: item.correct_option,
      board_id: item.board_id,
      medium_id: item.medium_id,
      class_id: item.class_id,
      subject_id: item.subject_id,
      chapter_id: item.chapter_id,
      question_typeid: item.question_typeid,
    }));

    setIsSubmitting(true);
    const toastId = showToast.loading(
      `Updating ${outputJsonArray.length} questions in database...`
    );

    try {
      const response = await axios.post(
        `${BoardMedium_URL}/bulk-update-answers`,
        { items: outputJsonArray }
      );

      const result = response.data;
      setSubmissionResult(result);
      showToast.dismiss(toastId);
      showToast.success(
        `Update complete: ${result.updated} updated, ${result.skipped} skipped.`,
        { duration: 4000 }
      );

      if (typeof onSuccess === "function") {
        onSuccess(result);
      }

      onClose();
    } catch (error) {
      console.error("Error updating answers:", error);
      showToast.dismiss(toastId);
      const errMsg =
        error.response?.data?.message ||
        error.message ||
        "Failed to update answers";
      showToast.error(`Update failed: ${errMsg}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFileName("");
    setParsedData([]);
    setSubmissionResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const filteredData = parsedData.filter((row) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      String(row.question_id).toLowerCase().includes(term) ||
      String(row.chapter_name).toLowerCase().includes(term) ||
      String(row.question_text).toLowerCase().includes(term) ||
      String(row.correct_answer).toLowerCase().includes(term)
    );
  });

  return (
    <Popup
      title="Upload & Manage Answers"
      onClose={onClose}
      maxWidth="1180px"
      minHeight="520px"
    >
      <div className="upload-answers-container">
        {/* Full Loader Overlay during submission */}
        {isSubmitting && (
          <div className="upload-answers-overlay">
            <div className="upload-answers-spinner" />
            <h4 className="upload-answers-loading-title">
              Updating {parsedData.length} Records in Database...
            </h4>
            <p className="upload-answers-loading-desc">
              Please wait while checking and applying correct answers...
            </p>
          </div>
        )}

        {/* Compact Context Badges */}
        <div className="upload-context-bar">
          <div className="upload-context-chips">
            <span className="upload-chip board">Board: {boardId || "-"}</span>
            <span className="upload-chip medium">
              Medium: {selectedMedium || "-"}
            </span>
            <span className="upload-chip class">Class: {selectedClass || "-"}</span>
            <span className="upload-chip subject">
              Subject: {selectedSubject || "-"}
            </span>
            {selectedChapter && (
              <span className="upload-chip chapter">
                Chapter: {selectedChapter}
              </span>
            )}
            <span className="upload-chip type">
              Type: {getTypeName(selectedType)}
            </span>
          </div>
        </div>

        {/* Submission Result Report Card */}
        {submissionResult && (
          <div className="upload-result-card">
            <div className="upload-result-header">
              <span className="upload-result-title">
                <span>🎉</span> {submissionResult.message}
              </span>
              <div className="upload-result-badges">
                <span className="upload-badge updated">
                  ✓ Updated: {submissionResult.updated}
                </span>
                <span
                  className={`upload-badge ${
                    submissionResult.skipped > 0
                      ? "skipped-warning"
                      : "skipped-neutral"
                  }`}
                >
                  ⚠ Skipped: {submissionResult.skipped}
                </span>
                <span className="upload-badge total">
                  Total: {submissionResult.total}
                </span>
              </div>
            </div>

            {submissionResult.breakdown && (
              <div className="upload-result-breakdown">
                <span>
                  • <strong>Already has answer:</strong>{" "}
                  {submissionResult.breakdown.skipped_already_has_correct || 0}
                </span>
                <span>
                  • <strong>Question not found:</strong>{" "}
                  {submissionResult.breakdown.skipped_question_not_found || 0}
                </span>
                <span>
                  • <strong>Option sort not found:</strong>{" "}
                  {submissionResult.breakdown.skipped_option_sort_not_found || 0}
                </span>
                <span>
                  • <strong>Invalid rows:</strong>{" "}
                  {submissionResult.breakdown.skipped_invalid_rows || 0}
                </span>
              </div>
            )}
          </div>
        )}

        {/* File Upload Dropzone Bar */}
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          className="upload-dropzone"
        >
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept=".xlsx, .xls, .csv"
            style={{ display: "none" }}
          />
          <div
            className="upload-dropzone-content"
            onClick={() => fileInputRef.current?.click()}
          >
            <span className="upload-dropzone-icon">📊</span>
            <div>
              <span
                className={`upload-dropzone-title ${
                  fileName ? "has-file" : ""
                }`}
              >
                {fileName
                  ? `File: ${fileName}`
                  : "Click or Drag & Drop Excel file (.xlsx, .xls, .csv)"}
              </span>
              <span className="upload-dropzone-sub">
                (Cols: Question ID, Chapter Name, Question, Option A-D, Correct Answer, Answer Explanation)
              </span>
            </div>
          </div>

          <div className="upload-dropzone-actions">
            <button
              onClick={() => fileInputRef.current?.click()}
              className="upload-btn-browse"
            >
              <span>📂</span> Browse File
            </button>
            {parsedData.length > 0 && (
              <button
                onClick={handleReset}
                className="upload-btn-clear"
                title="Clear all"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {isProcessing && (
          <div className="upload-loading-text">
            ⏳ Reading and parsing Excel file...
          </div>
        )}

        {/* Table Preview */}
        {parsedData.length > 0 && (
          <div className="upload-table-section">
            <div className="upload-table-toolbar">
              <div className="upload-table-title">
                Preview ({filteredData.length} of {parsedData.length} rows)
              </div>
              <input
                type="text"
                placeholder="Search preview..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="upload-table-search"
              />
            </div>

            <div className="upload-table-wrapper">
              <table className="upload-table">
                <thead>
                  <tr>
                    <th style={{ width: "35px" }}>#</th>
                    <th style={{ width: "75px" }}>Q.ID</th>
                    <th style={{ minWidth: "110px" }}>Chapter</th>
                    <th style={{ minWidth: "240px" }}>Question</th>
                    <th style={{ minWidth: "90px" }}>Opt A</th>
                    <th style={{ minWidth: "90px" }}>Opt B</th>
                    <th style={{ minWidth: "90px" }}>Opt C</th>
                    <th style={{ minWidth: "90px" }}>Opt D</th>
                    <th style={{ minWidth: "110px" }}>Answer</th>
                    <th style={{ width: "45px", textAlign: "center" }}>Del</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((row) => {
                    const originalIndex = parsedData.findIndex(
                      (item) => item.id === row.id
                    );
                    return (
                      <tr key={row.id}>
                        <td className="upload-cell-idx">{row.row_index}</td>
                        <td className="upload-cell-qid">{row.question_id || "-"}</td>
                        <td className="upload-cell-chapter" title={row.chapter_name}>
                          {row.chapter_name || "-"}
                        </td>
                        <td className="upload-cell-question" title={row.question_text}>
                          {row.question_text || "-"}
                        </td>
                        <td
                          className={`upload-cell-option ${
                            row.option_sort === 1 ? "selected" : ""
                          }`}
                          title={row.option_a}
                        >
                          {row.option_a || "-"}
                        </td>
                        <td
                          className={`upload-cell-option ${
                            row.option_sort === 2 ? "selected" : ""
                          }`}
                          title={row.option_b}
                        >
                          {row.option_b || "-"}
                        </td>
                        <td
                          className={`upload-cell-option ${
                            row.option_sort === 3 ? "selected" : ""
                          }`}
                          title={row.option_c}
                        >
                          {row.option_c || "-"}
                        </td>
                        <td
                          className={`upload-cell-option ${
                            row.option_sort === 4 ? "selected" : ""
                          }`}
                          title={row.option_d}
                        >
                          {row.option_d || "-"}
                        </td>
                        <td
                          className={`upload-cell-answer ${
                            row.correct_option ? "has-option" : ""
                          }`}
                        >
                          {row.correct_answer ||
                            (row.correct_option
                              ? `Option ${row.correct_option}`
                              : "-")}
                        </td>
                        <td className="upload-cell-del">
                          <button
                            type="button"
                            onClick={() => handleDeleteRow(originalIndex)}
                            className="upload-btn-del"
                            title="Delete row"
                          >
                            🗑
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Footer Actions */}
        <div className="upload-footer">
          <div className="upload-footer-summary">
            {parsedData.length > 0
              ? `Total ${parsedData.length} question(s) ready`
              : "Upload an Excel sheet to begin"}
          </div>

          <div className="upload-footer-actions">
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="upload-btn-close"
            >
              Close
            </button>
            <button
              onClick={handleSubmit}
              disabled={parsedData.length === 0 || isSubmitting}
              className={`upload-btn-submit ${
                parsedData.length > 0 && !isSubmitting ? "active" : "disabled"
              }`}
            >
              <span>✓</span> {isSubmitting ? "Updating..." : "Submit Answers"}
            </button>
          </div>
        </div>
      </div>
    </Popup>
  );
};

export default UploadAnswersPopup;
